// The CV is the data; Ansible's play/role/vars structure is the presentation.
export const overviewFile = 'jelmerfranken.nl/main.yml';
export const profileVarsFile = 'jelmerfranken.nl/vars/profile.yml';
export const roleDefinitions = [
  { id: 'engineer', title: ['Operations engineer', 'Operations engineer'], tag: 'work' },
  { id: 'developer', title: ['Development', 'Development'], tag: 'development' },
  { id: 'security', title: ['Security', 'Beveiliging'], tag: 'security' },
  { id: 'teammate', title: ['Collaboration', 'Samenwerking'], tag: 'teamwork' },
  { id: 'homelab', title: ['Homelab & interests', 'Homelab & interesses'], tag: 'interests' },
  { id: 'education', title: ['Education', 'Opleiding'], tag: 'education' },
].map(role => ({ ...role, name: `ansible-role-${role.id}`, path: `roles/ansible-role-${role.id}/defaults/main.yml` }));

// Emit the small, JSON-compatible YAML subset used by this profile.
export function yamlLines(value, indent = 0) {
  const prefix = ' '.repeat(indent);
  const scalar = item => JSON.stringify(item);
  if (Array.isArray(value)) {
    return value.flatMap(item => {
      if (item && typeof item === 'object') {
        const lines = yamlLines(item, indent + 2);
        return [prefix + '- ' + lines[0].slice(indent + 2), ...lines.slice(1)];
      }
      return [prefix + '- ' + scalar(item)];
    });
  }
  return Object.entries(value).flatMap(([key, item]) => {
    if (Array.isArray(item) && item.every(entry => entry === null || typeof entry !== 'object')) {
      return [`${prefix}${key}: [${item.map(scalar).join(', ')}]`];
    }
    if (item && typeof item === 'object') return [`${prefix}${key}:`, ...yamlLines(item, indent + 2)];
    return [`${prefix}${key}: ${scalar(item)}`];
  });
}

export function createProfileFiles(data) {
  const currentWork = data.work.find(work => !work.endDate) || data.work[0] || {};
  const names = index => (data.skills[index]?.skills || []).map(skill => skill.name);
  const technical = names(0);
  const languages = technical.filter(name => ['Python', 'JavaScript', 'Java'].includes(name));
  const infrastructure = technical.filter(name => !languages.includes(name));
  // These tools were explicitly confirmed by Jelmer during this redesign.
  const platforms = [...new Set(['Ansible', 'Docker', 'Terraform', ...infrastructure, ...names(1)])];
  const roleVars = {
    engineer: {
      engineer_position: currentWork.position || data.basics.label,
      engineer_company: currentWork.name || '',
      engineer_since: currentWork.startDate || '',
      engineer_tools: platforms,
      engineer_responsibilities: currentWork.highlights || [],
    },
    developer: { developer_languages: languages, developer_tools: ['Git', 'Docker', 'CI/CD'] },
    security: { security_skills: names(2) },
    teammate: { teammate_skills: names(3), teammate_languages: data.languages.map(item => ({ language: item.language, fluency: item.fluency })) },
    homelab: { homelab_platforms: ['Proxmox', 'Home Assistant'], homelab_skills: names(4), homelab_interests: data.interests.map(item => item.name) },
    education: { education_studies: data.education.map(item => ({ institution: item.institution, qualification: item.studyType, specialization: item.area, completed: item.endDate })) },
  };
  const playVars = {
    engineer: { engineer_position: currentWork.position || data.basics.label, engineer_company: currentWork.name || '', engineer_focus: ['Linux', 'Ansible', 'Docker', 'Terraform'] },
    developer: { developer_languages: languages },
    security: { security_skills: names(2) },
    teammate: { teammate_skills: names(3) },
    homelab: { homelab_platforms: ['Proxmox', 'Home Assistant'], homelab_interests: data.interests.map(item => item.name) },
    education: { education_specialization: data.education[0]?.area || '' },
  };
  const roles = roleDefinitions.map(role => ({ ...role, vars: roleVars[role.id] }));
  const play = [{
    name: data.basics.name,
    hosts: 'jelmerfranken.nl',
    vars_files: ['vars/profile.yml'],
    roles: roles.map(role => ({ role: role.name, vars: playVars[role.id], tags: [role.tag] })),
  }];
  const profile = {
    profile_name: data.basics.name,
    profile_location: data.basics.location.city,
    profile_country: data.basics.location.country,
    profile_email: data.basics.email,
    profile_about: data.basics.summary,
    profile_languages: data.languages.map(item => ({ language: item.language, fluency: item.fluency })),
  };
  const files = [
    { path: overviewFile, title: ['My personal playbook', 'Mijn persoonlijke playbook'], lines: ['---', ...yamlLines(play)] },
    { path: profileVarsFile, title: ['Profile variables', 'Profielvariabelen'], lines: ['---', ...yamlLines(profile)] },
    ...roles.map(role => ({ path: role.path, title: role.title, role: role.name, lines: ['---', ...yamlLines(role.vars)] })),
  ];
  return { files, roles };
}

export const skillFiles = [{ path: overviewFile }, { path: profileVarsFile }, ...roleDefinitions.map(role => ({ path: role.path }))];

export function buildSkillTree(files = skillFiles) {
  const root = [];
  files.forEach(file => {
    let children = root;
    const parts = file.path.split('/');
    parts.forEach((name, i) => {
      const path = parts.slice(0, i + 1).join('/');
      let node = children.find(item => item.path === path);
      if (!node) {
        node = { name, path, ...(i < parts.length - 1 ? { children: [] } : {}) };
        children.push(node);
      }
      children = node.children;
    });
  });
  return root;
}
export const skillTree = buildSkillTree();
export const folderPaths = skillFiles.flatMap(file => {
  const parts = file.path.split('/');
  return parts.slice(0, -1).map((_, i) => parts.slice(0, i + 1).join('/'));
});
export function parseStackCommand(value) {
  const command = value.trim().replace(/\s+/g, ' ');
  if (!command) return null;
  const file = skillFiles.find(item => `cat ${item.path}` === command);
  if (file) return { mode: 'file', path: file.path };
  if (['help', 'ls', 'tree', 'clear'].includes(command)) return { mode: command };
  return { mode: 'error', command };
}
export function stackCompletions(value) {
  return [...skillFiles.map(file => `cat ${file.path}`), 'help', 'ls', 'tree', 'clear'].filter(command => command.startsWith(value));
}
